import { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import MachineModelForm from '../components/MachineModelForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';
import { useDebounce } from '../hooks/useDebounce';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const API_PREFIX = '/internal/machine-models/global';

export default function AdminMachineModels() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showToast } = useToast();

  const debouncedSearch = useDebounce(search);

  const fetchModels = async () => {
    try {
      const params = { ownership: 'system', ...(debouncedSearch ? { q: debouncedSearch } : {}) };
      const res = await api.get('/auth/machine-models/by-vendor', { params });
      setGroups(res.data?.data || res.data || []);
    } catch {
      showToast('Failed to load machine models', 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fetch when search changes
  useEffect(() => { fetchModels(); }, [debouncedSearch]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast('System model deleted', 'success');
      setDeleteTarget(null);
      fetchModels();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingModel(null);
    fetchModels();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="System Machine Models"
        description="Manage global machine model presets available to all users"
        actions={
          <Button icon={PlusIcon} onClick={() => { setEditingModel(null); setShowForm(true); }}>
            Add System Model
          </Button>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search models..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : groups.length === 0 ? (
        <EmptyState icon={WrenchScrewdriverIcon} title="No system machine models" description="Add a global model preset" />
      ) : (
        groups.map((group) => (
          <div key={group.vendor || 'unknown'}>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">{group.vendor || 'Other'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {group.models?.map((model) => (
                <Card key={model._id} className="!p-0 overflow-hidden">
                  {/* Thumbnail */}
                  <div className="h-32 bg-slate-100 flex items-center justify-center">
                    {model.thumbnail?.url ? (
                      <img src={model.thumbnail.url} alt={model.name} className="h-full w-full object-cover" />
                    ) : (
                      <WrenchScrewdriverIcon className="h-10 w-10 text-slate-300" />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{model.name || model.fullName}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{model.vendor}</p>
                      </div>
                      <Badge variant="info" size="sm">System</Badge>
                    </div>
                    {model.buildVolume && (
                      <p className="text-xs text-slate-400 mt-2">
                        {model.buildVolume.x} x {model.buildVolume.y} x {model.buildVolume.z} mm
                      </p>
                    )}
                    {model.recommendedCostPerHour != null && (
                      <p className="text-xs text-slate-500 mt-1">{model.recommendedCostPerHour} €/h</p>
                    )}
                    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100">
                      <button onClick={() => { setEditingModel(model); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Edit">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteTarget(model)} className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors" title="Delete">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {showForm && (
        <MachineModelForm
          model={editingModel}
          apiPrefix={API_PREFIX}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingModel(null); }}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete System Machine Model"
          message={`Are you sure you want to delete "${deleteTarget.name}"? This will remove it for all users.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
